import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToastContext } from '../../contexts/ToastContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { useAvailablePlans, getVendorStatus, subscribe } from '../../services/subscriptionService';
// import api from '../../services/authService';
import PaymentModal from '../../components/shared/PaymentModal';
import { 
  Check, 
  X, 
  Calendar, 
  Users, 
  Image, 
  BarChart3, 
  Palette, 
  Headphones, 
  Code, 
  Clock, 
  MapPin,
  Star,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';

const VendorSubscription = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const { checkSubscriptionStatus } = useSubscription();
  const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
  const navigate = useNavigate();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [subscribingToFree, setSubscribingToFree] = useState(false);

  // Use React Query hooks
  const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } = useAvailablePlans();

  // Check if user is vendor and redirect if they have subscription (but allow access if expiring soon)
  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }

    // Add small delay to prevent immediate redirect when coming from modal
    const checkAndRedirect = async () => {
      if (hasActiveSubscription === true) {
        // Check if subscription is expiring soon (5 days or less)
        try {
          const response = await getVendorStatus();
          if (response.success && response.data.current_subscription) {
            const daysRemaining = response.data.current_subscription.days_remaining;
            
            // Allow access if subscription is expiring soon (5 days or less)
            if (daysRemaining <= 5) {
              console.log('Subscription expiring soon, allowing access to renewal page');
              return; // Don't redirect, allow access
            }
          }
        } catch (error) {
          console.error('Error checking subscription details:', error);
        }
        
        // User has active subscription that's not expiring soon, redirect to dashboard
        console.log('User has active subscription, redirecting to dashboard');
        navigate('/vendor', { replace: true });
        return;
      }
    };

    // Add delay to prevent immediate redirect from modal
    const timer = setTimeout(checkAndRedirect, 500);
    return () => clearTimeout(timer);
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const handleSelectPlan = async (plan) => {
    const isFree = parseFloat(plan.price) === 0;
    if (isFree) {
      try {
        setSubscribingToFree(true);
        const response = await subscribe({
          subscription_id: plan.id,
          payment_method: 'free'
        });
        if (response.success) {
          handlePaymentSuccess(response);
        } else {
          showError(response.message || 'Failed to subscribe to free plan');
        }
      } catch (err) {
        showError(err?.response?.data?.message || err?.message || 'Failed to subscribe to free plan');
      } finally {
        setSubscribingToFree(false);
      }
    } else {
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentSuccess = (data) => {
    success('Successfully subscribed! Welcome to your new plan.');
    
    // Update subscription status in context
    checkSubscriptionStatus();
    
    // Close modal
    setShowPaymentModal(false);
    
    // Immediately redirect to dashboard after successful subscription
    setTimeout(() => {
      navigate('/vendor', { replace: true });
    }, 1000);
  };

  const handlePaymentError = (error) => {
    showError(error || 'Payment failed. Please try again.');
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      analytics: BarChart3,
      custom_branding: Palette,
      priority_support: Headphones,
      api_access: Code,
      advanced_booking: Clock,
      multi_location: MapPin,
    };
    return icons[feature] || Check;
  };

  const renderFeature = (feature, value, label) => {
    const Icon = getFeatureIcon(feature);
    return (
      <div className="flex items-center space-x-2">
        {value ? (
          <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
        ) : (
          <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
        )}
        <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
        <span className="text-xs sm:text-sm text-gray-600">{label}</span>
      </div>
    );
  };

  // Extract subscriptions from hook data
  const subscriptions = subscriptionsData?.data || [];

  // Handle subscriptions error
  useEffect(() => {
    if (subscriptionsError) {
      showError(subscriptionsError.message || 'Failed to load subscription plans');
    }
  }, [subscriptionsError, showError]);

  // Show loading while checking subscription status or loading plans
  if (isChecking || subscriptionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isChecking ? 'Checking subscription status...' : 'Loading subscription plans...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-12">
          {/* Back to Site Button */}
          <div className="mb-4 sm:mb-6">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Back to Site
            </button>
          </div>
          
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
            Choose Your Subscription Plan
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Pick a plan to start offering your services.
          </p>
        </div>


        {/* Alert if no subscription */}
        {hasActiveSubscription === false && (
          <div className="mb-4 sm:mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 flex items-start sm:items-center space-x-2 sm:space-x-3">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <h3 className="text-xs sm:text-sm font-medium text-yellow-800">
                  Subscription Required
                </h3>
                <p className="text-xs sm:text-sm text-yellow-700 mt-1">
                  You need an active subscription to offer services and access vendor features. Choose a plan below to get started.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-12">
          {subscriptions.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden ${
                plan.slug === 'premium' ? 'ring-2 ring-primary-500 md:scale-105' : ''
              }`}
            >
              {plan.slug === 'premium' && (
                <div className="absolute top-0 right-0 bg-primary-500 text-white px-2 sm:px-4 py-1 text-xs sm:text-sm font-medium rounded-bl-lg">
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  Popular
                </div>
              )}
              
              <div className="p-4 sm:p-6 lg:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">{plan.name}</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{plan.description}</p>
                
                <div className="mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                    {parseFloat(plan.price) === 0 ? 'Free' : `$${plan.price}`}
                  </span>
                  {parseFloat(plan.price) > 0 && plan.duration_days && (
                    <span className="text-sm sm:text-base text-gray-600 ml-1">
                      /{plan.duration_days === 30 ? 'month' : `for ${plan.duration_days} days`}
                    </span>
                  )}
                </div>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={subscribingToFree}
                  className={`w-full py-2.5 sm:py-3 px-4 rounded-lg text-sm sm:text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    plan.slug === 'premium'
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {subscribingToFree ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Subscribing...
                    </span>
                  ) : parseFloat(plan.price) === 0 ? (
                    `Get ${plan.name} Free`
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </button>
              </div>

              <div className="border-t border-gray-200 p-4 sm:p-6 lg:p-8">
                <h4 className="font-medium text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">What's included:</h4>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center space-x-2">
                    <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600">
                      {plan.max_bookings_per_month ? 
                        `Up to ${plan.max_bookings_per_month} bookings/month` : 
                        'Unlimited bookings'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Image className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600">
                      {plan.max_services ? 
                        `Up to ${plan.max_services} services` : 
                        'Unlimited services'
                      }
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-600">
                      {plan.max_portfolio_items ? 
                        `Up to ${plan.max_portfolio_items} portfolio items` : 
                        'Unlimited portfolio'
                      }
                    </span>
                  </div>

                  {renderFeature('analytics', plan.feature_analytics, 'Advanced Analytics')}
                  {renderFeature('custom_branding', plan.feature_custom_branding, 'Custom Branding')}
                  {renderFeature('priority_support', plan.feature_priority_support, 'Priority Support')}
                  {/* {renderFeature('api_access', plan.feature_api_access, 'API Access')} */}
                  {renderFeature('advanced_booking', plan.feature_advanced_booking, 'Advanced Booking')}
                  {renderFeature('multi_location', plan.feature_multi_location, 'Multi-Location')}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          selectedPlan={selectedPlan}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </div>
    </div>
  );
};

export default VendorSubscription;
